import React, { useEffect, useRef, useState } from 'react';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { useAppContext } from 'providers/AppProvider';
import { TooltipComponent, TooltipComponentOption } from 'echarts/components';
import { PieChart, PieSeriesOption } from 'echarts/charts';
import { ECharts, EChartsCoreOption } from 'echarts/core';
import { fetchHVACallingTodaySuccess } from 'services/hvacallingmanagment.service';
import { fetchHVACallingTodaySuccessInbound } from 'services/hvacallingmanagment.service';

echarts.use([TooltipComponent, PieChart]);

interface DialingSuccessChartSmallProps {
  campaignid: number;
  campaignType?: 'outbound' | 'inbound';
}

interface FetchedData {
  description: string;
  total_count: number;
}

const getDefaultOptions = (
  getThemeColor: (name: string) => string,
  addAlphaToHexColor: (hex: string, alpha: number) => string,
  isDark: boolean,
  data: { value: number; name: string }[],
  campaignType: 'outbound' | 'inbound'
): EChartsCoreOption => ({
  color:
    campaignType === 'inbound'
      ? [
          getThemeColor('success-light'),
          !isDark
            ? getThemeColor('primary')
            : addAlphaToHexColor(getThemeColor('primary-dark'), 0.8)
        ]
      : [
          getThemeColor('success-light'),
          !isDark
            ? getThemeColor('primary-lighter')
            : getThemeColor('primary-lighter'),
          !isDark
            ? getThemeColor('primary-light')
            : getThemeColor('primary-light'),
          !isDark
            ? getThemeColor('primary')
            : addAlphaToHexColor(getThemeColor('primary-dark'), 0.8),
          !isDark
            ? getThemeColor('primary-darker')
            : getThemeColor('primary-darker')
        ],
  tooltip: {
    show: false,
    trigger: 'item',
    // eslint-disable-next-line
    backgroundColor: !isDark ? 'rgba(240, 240, 240, 0.9)' : 'rgba(49, 55, 74, 0.9)',
    textStyle: {
      color: !isDark ? '#202020' : '#ccc',
      fontFamily: 'Nunito Sans',
      fontSize: 14
    }
  } as TooltipComponentOption,
  responsive: true,
  maintainAspectRatio: false,

  series: [
    {
      name: 'Calls',
      type: 'pie',
      radius: ['78%', '93%'],
      startAngle: 90,
      avoidLabelOverlap: false,
      itemStyle: {
        borderRadius: 5,
        // eslint-disable-next-line
        borderColor: !isDark ? 'rgba(240, 240, 240, 0.8)' : 'rgba(49, 55, 74, 0.8)',
        borderWidth: 1
      },
      label: {
        show: true,
        position: 'center',
        formatter: '{x|{d}%} \n {y|{b}}',
        rich: {
          x: {
            fontSize: 16,
            fontWeight: 800,
            color: getThemeColor('tertiary-color'),
            padding: [10, 0, 0, 5]
          },
          y: {
            fontSize: 10,
            color: getThemeColor('tertiary-color'),
            fontWeight: 500,
            padding: [0, 5, 0, 0]
          }
        }
      },
      emphasis: {
        label: {
          show: true,
          fontSize: '16',
          fontWeight: 'bold',
          // eslint-disable-next-line
          backgroundColor: !isDark ? 'rgba(255, 255, 255, 1)' : 'rgba(20, 24, 36, 1)',
          borderRadius: 5,
          padding: 5
        }
      },
      labelLine: {
        show: false
      },
      data
    } as PieSeriesOption
  ],
  grid: {
    bottom: 0,
    top: 0,
    left: 0,
    right: 0,
    containLabel: false
  }
});

const DialingSuccessChartSmall: React.FC<DialingSuccessChartSmallProps> = ({
  campaignid,
  campaignType = 'outbound'
}) => {
  const {
    getThemeColor,
    addAlphaToHexColor,
    config: { isDark }
  } = useAppContext();
  const [data, setData] = useState<{ value: number; name: string }[]>([]);
  const chartRef = useRef<ReactEChartsCore>(null); // Ref para el componente

  const getDescriptionsMap = (): { [key: string]: string } => {
    if (campaignType === 'inbound') {
      return {
        handled: 'Handled',
        transferred: 'Transferred'
      };
    }

    // Outbound (default)
    return {
      answer: 'Answer',
      busy: 'Busy',
      noanswer: 'No answer',
      noline: 'No line',
      lineissue: 'Line issue'
    };
  };

  const fetchData = async () => {
    try {
      let response: FetchedData[];

      if (campaignType === 'inbound') {
        // Necesitarás crear este nuevo endpoint
        response = await fetchHVACallingTodaySuccessInbound({
          campaign: campaignid,
          interval: 3
        });
      } else {
        response = await fetchHVACallingTodaySuccess({
          campaign: campaignid,
          interval: 3
        });
      }

      const descriptionsMap = getDescriptionsMap();

      const fetchedData = response.map((item: FetchedData) => ({
        value: item.total_count,
        name: descriptionsMap[item.description] || item.description
      }));

      // Ordenar los datos según el tipo de campaña
      const orderedData =
        campaignType === 'inbound'
          ? [
              {
                name: 'Handled',
                value: fetchedData.find(d => d.name === 'Handled')?.value || 0
              },
              {
                name: 'Transferred',
                value:
                  fetchedData.find(d => d.name === 'Transferred')?.value || 0
              }
            ]
          : [
              {
                name: 'Answer',
                value: fetchedData.find(d => d.name === 'Answer')?.value || 0
              },
              {
                name: 'Busy',
                value: fetchedData.find(d => d.name === 'Busy')?.value || 0
              },
              {
                name: 'No answer',
                value: fetchedData.find(d => d.name === 'No answer')?.value || 0
              },
              {
                name: 'No line',
                value: fetchedData.find(d => d.name === 'No line')?.value || 0
              },
              {
                name: 'Line issue',
                value:
                  fetchedData.find(d => d.name === 'Line issue')?.value || 0
              }
            ];

      setData(orderedData);
    } catch (error) {
      console.error('Error fetching dialing success data:', error);
    }
  };

  useEffect(() => {
    fetchData();

    const intervalId = setInterval(fetchData, 10000); // Actualiza cada 10 segundos

    return () => clearInterval(intervalId); // Limpia el intervalo al desmontar el componente
  }, [campaignid, campaignType]);

  useEffect(() => {
    const chart = chartRef.current?.getEchartsInstance(); // Obtiene la instancia del gráfico

    if (chart) {
      chart.dispatchAction({
        type: 'highlight',
        seriesIndex: 0,
        dataIndex: 0
      });
      chart.dispatchAction({
        type: 'showTip',
        seriesIndex: 0,
        dataIndex: 0
      });
    }
  }, [data]);

  const onChartReady = (chart: ECharts) => {
    // Resaltar "Answer" por defecto
    chart.dispatchAction({
      type: 'highlight',
      seriesIndex: 0,
      dataIndex: 0
    });
    chart.dispatchAction({
      type: 'showTip',
      seriesIndex: 0,
      dataIndex: 0
    });

    // eslint-disable-next-line
    chart.on('mouseover', (params: any) => {
      if (params.name !== 'Answer') {
        chart.dispatchAction({
          type: 'downplay',
          seriesIndex: 0,
          dataIndex: 0
        });
        chart.dispatchAction({
          type: 'highlight',
          seriesIndex: 0,
          dataIndex: params.dataIndex
        });
        chart.dispatchAction({
          type: 'showTip',
          seriesIndex: 0,
          dataIndex: params.dataIndex
        });
      }
    });
    // eslint-disable-next-line
    chart.on('mouseout', (params: any) => {
      chart.dispatchAction({
        type: 'downplay',
        seriesIndex: 0,
        dataIndex: params.dataIndex
      });
      chart.dispatchAction({
        type: 'highlight',
        seriesIndex: 0,
        dataIndex: 0
      });
      chart.dispatchAction({
        type: 'showTip',
        seriesIndex: 0,
        dataIndex: 0
      });
    });
  };

  return (
    <ReactEChartsCore
      ref={chartRef} // Asigna el ref al componente
      echarts={echarts}
      // eslint-disable-next-line
      option={getDefaultOptions(getThemeColor, addAlphaToHexColor, isDark, data, campaignType)}
      style={{ height: '120px', width: '120px' }}
      onChartReady={onChartReady}
    />
  );
};

export default DialingSuccessChartSmall;
